import { create } from 'zustand'
import { uid } from '../lib/cn'
import { diasHasta, estadoStock } from '../lib/format'
import { seguimientosPendientes } from '../lib/metricas'
import type {
  AccionBitacora,
  Ajustes,
  Bitacora,
  Cliente,
  ConfirmacionCliente,
  Devolucion,
  DiaDespacho,
  EstadoPedido,
  Gasto,
  LineaVenta,
  Lote,
  Movimiento,
  Notificacion,
  Producto,
  Usuario,
  Venta,
} from '../types'
import type { Adapter, Snapshot, Tabla } from '../data/adapter'
import { snapshotVacio } from '../data/adapter'
import { localAdapter } from '../data/localAdapter'
import { supabaseAdapter, supabaseConfigurado } from '../data/supabaseAdapter'
import { construirSemilla } from '../data/seed'

interface EstadoDatos extends Snapshot {
  listo: boolean
  cargando: boolean
  error: string | null
  adaptador: Adapter
  /** Nombre del backend activo, para mostrarlo en Ajustes. */
  backend: 'local' | 'supabase'

  inicializar: () => Promise<void>
  recargar: () => Promise<void>

  registrarVenta: (entrada: EntradaVenta, autor: Usuario) => Promise<Venta>
  cambiarEstadoVenta: (ventaId: string, estado: EstadoPedido, autor: Usuario) => Promise<void>
  marcarPagada: (ventaId: string, autor: Usuario) => Promise<void>
  anularVenta: (ventaId: string, autor: Usuario) => Promise<void>
  /** El despachador confirma que recibió y revisó el pedido. Si pasa
   *  algo (falta stock, dirección mala, etc.) manda una nota en
   *  `erroDespacho` en vez de dejarla vacía. */
  revisarDespacho: (ventaId: string, erroDespacho: string | undefined, autor: Usuario) => Promise<void>
  /** Resultado de llamar o escribir al cliente antes de salir. Si no
   *  confirma, el pedido se corre solo al próximo día de reparto. */
  confirmarCliente: (
    ventaId: string,
    resultado: ConfirmacionCliente,
    autor: Usuario,
  ) => Promise<void>
  /** Mueve un pedido a otro día de entrega, sin tocar nada más. */
  cambiarFechaDespacho: (
    ventaId: string,
    diaDespacho: DiaDespacho,
    fechaDespacho: string,
    autor: Usuario,
  ) => Promise<void>

  ingresarStock: (entrada: EntradaStock, autor: Usuario) => Promise<void>
  /** Registra la llegada de un pedido con varios productos de una vez
   *  —cada línea crea su propio lote— en lugar de cargarlos uno a uno. */
  registrarOrdenIngreso: (entrada: EntradaOrdenIngreso, autor: Usuario) => Promise<void>
  ajustarStock: (productoId: string, nuevoStock: number, motivo: string, autor: Usuario) => Promise<void>
  registrarDevolucion: (entrada: EntradaDevolucion, autor: Usuario) => Promise<void>

  guardarProducto: (producto: Producto, autor: Usuario) => Promise<void>
  eliminarProducto: (id: string, autor: Usuario) => Promise<void>
  guardarCliente: (cliente: Cliente, autor: Usuario) => Promise<void>
  eliminarCliente: (id: string, autor: Usuario) => Promise<void>
  guardarUsuario: (usuario: Usuario, autor: Usuario) => Promise<void>
  eliminarUsuario: (id: string, autor: Usuario) => Promise<void>
  guardarAjustes: (ajustes: Ajustes, autor: Usuario) => Promise<void>

  guardarGasto: (gasto: Gasto, autor: Usuario) => Promise<void>
  eliminarGasto: (id: string, autor: Usuario) => Promise<void>

  anotar: (autor: Usuario, accion: AccionBitacora, entidad: string, detalle: string, entidadId?: string) => Promise<void>
  exportarRespaldo: () => Snapshot
  restaurarRespaldo: (snapshot: Snapshot) => Promise<void>
  cargarDemo: () => Promise<void>
  borrarTodo: () => Promise<void>
}

export interface EntradaVenta {
  clienteId: string
  lineas: { productoId: string; cantidad: number; precioUnitario: number }[]
  metodoPago: Venta['metodoPago']
  estado: EstadoPedido
  observaciones?: string
  vencimientoPago?: string
  diaDespacho?: Venta['diaDespacho']
  fechaDespacho?: string
  direccionDespacho?: string
}

export interface EntradaStock {
  productoId: string
  cantidad: number
  proveedor: string
  fecha: string
  costo: number
  vencimiento?: string
  codigoLote?: string
  recibidoPor?: string
  observaciones?: string
}

/** Una orden de ingreso: llega un camión con varios productos a la
 *  vez, cada uno con su propio lote, costo y vencimiento, pero
 *  comparten fecha, proveedor y quién recibió. */
export interface EntradaOrdenIngreso {
  fecha: string
  proveedor: string
  recibidoPor: string
  observaciones?: string
  lineas: {
    productoId: string
    cantidad: number
    costo: number
    vencimiento?: string
    codigoLote?: string
  }[]
}

export interface EntradaDevolucion {
  ventaId: string
  productoId: string
  cantidad: number
  motivo: string
  reponeStock: boolean
}

const ahora = () => new Date().toISOString()

/** Deja las colecciones cronológicas de la más reciente a la más
 *  antigua.
 *
 *  Hace falta porque ningún backend garantiza el orden: IndexedDB
 *  devuelve por clave primaria y Supabase sin ORDER BY no promete nada.
 *  Toda la app asume "lo último primero" —últimas ventas, historial de
 *  movimientos, bitácora—, así que se normaliza una vez al cargar en vez
 *  de repetir el sort en cada pantalla. */
function ordenar(s: Snapshot): Snapshot {
  const recientePrimero = (a: { fecha: string }, b: { fecha: string }) =>
    b.fecha.localeCompare(a.fecha)
  return {
    ...s,
    ventas: [...s.ventas].sort(recientePrimero),
    movimientos: [...s.movimientos].sort(recientePrimero),
    bitacora: [...s.bitacora].sort(recientePrimero),
    devoluciones: [...s.devoluciones].sort(recientePrimero),
    gastos: [...s.gastos].sort(recientePrimero),
    lotes: [...s.lotes].sort((a, b) => b.ingresado.localeCompare(a.ingresado)),
  }
}

/** Cuando Supabase está configurado se usa; si no, la app corre igual
 *  contra IndexedDB. Nunca queda inutilizable por falta de credenciales. */
const elegirAdaptador = (): Adapter => (supabaseConfigurado ? supabaseAdapter : localAdapter)

export const useDatos = create<EstadoDatos>((set, get) => ({
  ...snapshotVacio(),
  listo: false,
  cargando: false,
  error: null,
  adaptador: elegirAdaptador(),
  backend: supabaseConfigurado ? 'supabase' : 'local',

  async inicializar() {
    if (get().cargando) return
    set({ cargando: true, error: null })
    let adaptador = get().adaptador
    try {
      if (adaptador.nombre === 'supabase' && !(await adaptador.disponible())) {
        // Credenciales presentes pero la base no responde: se sigue en
        // local para no dejar al vendedor sin herramienta.
        adaptador = localAdapter
        set({
          adaptador,
          backend: 'local',
          error: 'No se pudo conectar con Supabase. Trabajando con los datos de este dispositivo.',
        })
      }
      let snapshot = await adaptador.cargar()
      // Sembrar datos de demostración solo tiene sentido en el dispositivo
      // local, donde "vacío" significa de verdad una instalación nueva.
      // En Supabase, una tabla "usuarios" vacía casi siempre significa que
      // aún falta insertar la primera cuenta (ver supabase/schema.sql) —
      // nunca hay que rellenar una base de datos compartida con productos
      // y ventas falsas sin que el administrador lo pida explícitamente.
      if (!snapshot.usuarios.length && adaptador.nombre === 'local') {
        snapshot = construirSemilla()
        await adaptador.reemplazarTodo(snapshot)
      }
      set({ ...ordenar(snapshot), listo: true, cargando: false })
    } catch (e) {
      set({ cargando: false, error: e instanceof Error ? e.message : 'Error al cargar los datos.' })
    }
  },

  async recargar() {
    const snapshot = await get().adaptador.cargar()
    set({ ...ordenar(snapshot) })
  },

  /* --------------------------- Ventas --------------------------- */

  async registrarVenta(entrada, autor) {
    const { productos, lotes, ajustes, clientes, ventas, adaptador } = get()
    const cliente = clientes.find((c) => c.id === entrada.clienteId)
    if (!cliente) throw new Error('Selecciona un cliente para continuar.')
    if (!entrada.lineas.length) throw new Error('Agrega al menos un producto.')

    const productosTocados: Producto[] = []
    const lotesTocados: Lote[] = []
    const movimientos: Movimiento[] = []
    const lineas: LineaVenta[] = []
    const fecha = ahora()
    const ventaId = uid('v')

    for (const l of entrada.lineas) {
      const p = productos.find((x) => x.id === l.productoId)
      if (!p) throw new Error('Uno de los productos ya no existe.')
      const disponible = p.stock - p.reservado
      if (l.cantidad > disponible) {
        throw new Error(`${p.nombre}: solo quedan ${disponible} disponibles.`)
      }

      const actualizado = productosTocados.find((x) => x.id === p.id) ?? { ...p }
      actualizado.stock -= l.cantidad
      if (!productosTocados.some((x) => x.id === p.id)) productosTocados.push(actualizado)

      // FEFO: se descuenta primero del lote que vence antes, para que lo
      // más antiguo salga primero de la cámara.
      let restante = l.cantidad
      const suyos = lotes
        .filter((lo) => lo.productoId === p.id && lo.cantidad > 0)
        .sort((a, b) => (a.vencimiento ?? '9999').localeCompare(b.vencimiento ?? '9999'))
      let loteUsado: string | undefined
      for (const lo of suyos) {
        if (restante <= 0) break
        const toma = Math.min(lo.cantidad, restante)
        const copia = lotesTocados.find((x) => x.id === lo.id) ?? { ...lo }
        copia.cantidad -= toma
        if (!lotesTocados.some((x) => x.id === lo.id)) lotesTocados.push(copia)
        restante -= toma
        loteUsado ??= lo.id
      }

      lineas.push({
        productoId: p.id,
        nombre: p.nombre,
        codigo: p.codigo,
        cantidad: l.cantidad,
        precioUnitario: l.precioUnitario,
        costoUnitario: p.precioCosto,
        subtotal: l.precioUnitario * l.cantidad,
        loteId: loteUsado,
      })

      movimientos.push({
        id: uid('m'),
        productoId: p.id,
        tipo: 'venta',
        cantidad: -l.cantidad,
        stockResultante: actualizado.stock,
        loteId: loteUsado,
        referenciaId: ventaId,
        usuarioId: autor.id,
        usuarioNombre: autor.nombre,
        fecha,
      })
    }

    const total = lineas.reduce((s, l) => s + l.subtotal, 0)
    const costoTotal = lineas.reduce((s, l) => s + l.costoUnitario * l.cantidad, 0)
    const ultimoFolio = ventas.reduce((max, v) => {
      const n = Number(v.folio.replace(/\D/g, ''))
      return Number.isFinite(n) && n > max ? n : max
    }, 1000)

    const venta: Venta = {
      id: ventaId,
      folio: `V-${ultimoFolio + 1}`,
      clienteId: cliente.id,
      clienteNombre: [cliente.nombre, cliente.apellido].filter(Boolean).join(' '),
      clienteTipo: cliente.tipo,
      usuarioId: autor.id,
      usuarioNombre: autor.nombre,
      lineas,
      total,
      costoTotal,
      utilidad: total - costoTotal,
      // Se calcula sobre la utilidad (venta menos costo), no sobre el
      // total vendido —la comisión del 30% es sobre ganancia real, no
      // sobre ingresos. Se usa la tasa del vendedor en este momento, no
      // la que tenga después: así un cambio de tasa no reescribe
      // comisiones ya ganadas.
      comision: Math.round((total - costoTotal) * ((autor.comision || 0) / 100)),
      metodoPago: entrada.metodoPago,
      estado: entrada.estado,
      pagado: entrada.metodoPago !== 'credito',
      vencimientoPago:
        entrada.metodoPago === 'credito'
          ? (entrada.vencimientoPago ??
            new Date(Date.now() + ajustes.diasCredito * 86_400_000).toISOString().slice(0, 10))
          : undefined,
      // Si la venta ya nace "entregada" (el caso más común: se entrega
      // en el momento), la fecha de entrega es la misma de la venta.
      fechaEntrega: entrada.estado === 'entregada' ? fecha : undefined,
      diaDespacho: entrada.diaDespacho,
      fechaDespacho: entrada.fechaDespacho,
      direccionDespacho: entrada.direccionDespacho,
      observaciones: entrada.observaciones,
      fecha,
    }

    // Copia de cómo estaban los productos y lotes tocados ANTES de
    // descontarles el stock: si el guardado falla hay que devolver todo
    // a este punto.
    const productosPrevios = productosTocados
      .map((t) => productos.find((p) => p.id === t.id))
      .filter((p): p is Producto => !!p)
    const lotesPrevios = lotesTocados
      .map((t) => lotes.find((l) => l.id === t.id))
      .filter((l): l is Lote => !!l)

    set((s) => ({
      ventas: [venta, ...s.ventas],
      productos: s.productos.map((p) => productosTocados.find((x) => x.id === p.id) ?? p),
      lotes: s.lotes.map((l) => lotesTocados.find((x) => x.id === l.id) ?? l),
      movimientos: [...movimientos, ...s.movimientos],
    }))

    // La pantalla se actualiza al tiro (arriba) para que vender sea
    // instantáneo aunque la señal esté mala, pero si la escritura falla
    // hay que deshacer eso: si no, queda una venta fantasma —visible en
    // la app, con el stock ya descontado— que en la base de datos no
    // existe y que no aparece en Despachos. Eso es peor que un error:
    // el vendedor cree que vendió y nadie va a despachar ese pedido.
    try {
      await Promise.all([
        adaptador.guardar('ventas', [venta]),
        adaptador.guardar('productos', productosTocados),
        adaptador.guardar('lotes', lotesTocados),
        adaptador.guardar('movimientos', movimientos),
      ])
    } catch (err) {
      const idsMovimientos = new Set(movimientos.map((m) => m.id))
      set((s) => ({
        ventas: s.ventas.filter((v) => v.id !== ventaId),
        productos: s.productos.map((p) => productosPrevios.find((x) => x.id === p.id) ?? p),
        lotes: s.lotes.map((l) => lotesPrevios.find((x) => x.id === l.id) ?? l),
        movimientos: s.movimientos.filter((m) => !idsMovimientos.has(m.id)),
      }))
      const detalle = err instanceof Error ? err.message : String(err)
      throw new Error(`La venta NO quedó registrada: ${detalle}`)
    }

    await get().anotar(autor, 'venta', 'venta', `${venta.folio} · ${venta.clienteNombre}`, venta.id)
    return venta
  },

  async cambiarEstadoVenta(ventaId, estado, autor) {
    const venta = get().ventas.find((v) => v.id === ventaId)
    if (!venta) return
    const actualizada: Venta = {
      ...venta,
      estado,
      // Se registra la primera vez que llega a "entregada"; si ya
      // estaba marcada y alguien la mueve hacia atrás y adelante, la
      // fecha real de la primera entrega no se pierde.
      fechaEntrega: estado === 'entregada' ? (venta.fechaEntrega ?? ahora()) : venta.fechaEntrega,
    }
    set((s) => ({ ventas: s.ventas.map((v) => (v.id === ventaId ? actualizada : v)) }))
    await get().adaptador.guardar('ventas', [actualizada])
    await get().anotar(autor, 'editar', 'venta', `${venta.folio} pasó a ${estado}`, ventaId)
  },

  async revisarDespacho(ventaId, erroDespacho, autor) {
    const venta = get().ventas.find((v) => v.id === ventaId)
    if (!venta) return
    // OJO: mandar '' y no undefined para "sin problema" —Supabase hace
    // upsert con JSON, y JSON.stringify borra las claves en undefined
    // del payload, así que la columna se quedaría con el valor viejo.
    const actualizada: Venta = { ...venta, revisadoDespacho: true, erroDespacho: erroDespacho || '' }
    set((s) => ({ ventas: s.ventas.map((v) => (v.id === ventaId ? actualizada : v)) }))
    await get().adaptador.guardar('ventas', [actualizada])
    await get().anotar(
      autor,
      'editar',
      'venta',
      erroDespacho
        ? `${venta.folio} revisado con problema: ${erroDespacho}`
        : `${venta.folio} recibido y revisado por el despachador`,
      ventaId,
    )
  },

  async confirmarCliente(ventaId, resultado, autor) {
    const venta = get().ventas.find((v) => v.id === ventaId)
    if (!venta) return

    let actualizada: Venta = { ...venta, confirmacionCliente: resultado }
    let detalle = `${venta.folio}: el cliente confirmó la entrega`

    // Si no hubo confirmación, el pedido no sale hoy: se corre solo al
    // próximo día de reparto para que no quede colgado ni haya que
    // acordarse a mano.
    if (resultado !== 'confirmado') {
      const { siguienteDiaReparto } = await import('../lib/geo')
      const siguiente = siguienteDiaReparto(venta.fechaDespacho)
      actualizada = {
        ...actualizada,
        diaDespacho: siguiente.dia,
        fechaDespacho: siguiente.fecha,
        vecesPospuesto: (venta.vecesPospuesto ?? 0) + 1,
        // Al cambiar de día vuelve a empezar: hay que revisarlo y
        // confirmarlo de nuevo cuando llegue esa fecha.
        revisadoDespacho: false,
        confirmacionCliente: null,
      }
      const motivo = resultado === 'no_contesto' ? 'no contestó' : 'no confirmó'
      detalle = `${venta.folio}: ${motivo}, se corre al ${siguiente.dia} ${siguiente.fecha}`
    }

    set((s) => ({ ventas: s.ventas.map((v) => (v.id === ventaId ? actualizada : v)) }))
    await get().adaptador.guardar('ventas', [actualizada])
    await get().anotar(autor, 'editar', 'venta', detalle, ventaId)
  },

  async cambiarFechaDespacho(ventaId, diaDespacho, fechaDespacho, autor) {
    const venta = get().ventas.find((v) => v.id === ventaId)
    if (!venta) return
    const actualizada: Venta = {
      ...venta,
      diaDespacho,
      fechaDespacho,
      // Cambió el día: lo que ya se había revisado o confirmado para la
      // fecha anterior deja de valer.
      revisadoDespacho: false,
      confirmacionCliente: null,
    }
    set((s) => ({ ventas: s.ventas.map((v) => (v.id === ventaId ? actualizada : v)) }))
    await get().adaptador.guardar('ventas', [actualizada])
    await get().anotar(
      autor,
      'editar',
      'venta',
      `${venta.folio}: entrega movida al ${fechaDespacho}`,
      ventaId,
    )
  },

  async marcarPagada(ventaId, autor) {
    const venta = get().ventas.find((v) => v.id === ventaId)
    if (!venta) return
    const actualizada = { ...venta, pagado: true }
    set((s) => ({ ventas: s.ventas.map((v) => (v.id === ventaId ? actualizada : v)) }))
    await get().adaptador.guardar('ventas', [actualizada])
    await get().anotar(autor, 'editar', 'cobranza', `${venta.folio} quedó pagada`, ventaId)
  },

  async anularVenta(ventaId, autor) {
    const { ventas, productos, adaptador } = get()
    const venta = ventas.find((v) => v.id === ventaId)
    if (!venta || venta.estado === 'anulada') return

    // Anular devuelve la mercadería al stock.
    const tocados: Producto[] = []
    const movimientos: Movimiento[] = []
    for (const l of venta.lineas) {
      const p = productos.find((x) => x.id === l.productoId)
      if (!p) continue
      const copia = tocados.find((x) => x.id === p.id) ?? { ...p }
      copia.stock += l.cantidad
      if (!tocados.some((x) => x.id === p.id)) tocados.push(copia)
      movimientos.push({
        id: uid('m'),
        productoId: p.id,
        tipo: 'ajuste',
        cantidad: l.cantidad,
        stockResultante: copia.stock,
        referenciaId: ventaId,
        usuarioId: autor.id,
        usuarioNombre: autor.nombre,
        observaciones: `Anulación de ${venta.folio}`,
        fecha: ahora(),
      })
    }

    const actualizada: Venta = { ...venta, estado: 'anulada' }
    set((s) => ({
      ventas: s.ventas.map((v) => (v.id === ventaId ? actualizada : v)),
      productos: s.productos.map((p) => tocados.find((x) => x.id === p.id) ?? p),
      movimientos: [...movimientos, ...s.movimientos],
    }))
    await Promise.all([
      adaptador.guardar('ventas', [actualizada]),
      adaptador.guardar('productos', tocados),
      adaptador.guardar('movimientos', movimientos),
    ])
    await get().anotar(autor, 'eliminar', 'venta', `Se anuló ${venta.folio}`, ventaId)
  },

  /* --------------------------- Stock --------------------------- */

  async ingresarStock(entrada, autor) {
    const { productos, adaptador, lotes } = get()
    const p = productos.find((x) => x.id === entrada.productoId)
    if (!p) throw new Error('El producto no existe.')
    if (entrada.cantidad <= 0) throw new Error('La cantidad debe ser mayor que cero.')

    const actualizado: Producto = {
      ...p,
      stock: p.stock + entrada.cantidad,
      // El último costo pagado manda: es el que refleja el margen real.
      precioCosto: entrada.costo > 0 ? entrada.costo : p.precioCosto,
    }

    const lote: Lote = {
      id: uid('l'),
      productoId: p.id,
      codigo:
        entrada.codigoLote?.trim() ||
        `L${new Date().getFullYear()}-${String(lotes.length + 1).padStart(3, '0')}`,
      cantidad: entrada.cantidad,
      cantidadInicial: entrada.cantidad,
      vencimiento: entrada.vencimiento || undefined,
      costo: entrada.costo || p.precioCosto,
      proveedor: entrada.proveedor,
      recibidoPor: entrada.recibidoPor,
      ingresado: entrada.fecha ? new Date(entrada.fecha).toISOString() : ahora(),
    }

    const movimiento: Movimiento = {
      id: uid('m'),
      productoId: p.id,
      tipo: 'ingreso',
      cantidad: entrada.cantidad,
      stockResultante: actualizado.stock,
      loteId: lote.id,
      proveedor: entrada.proveedor,
      costo: entrada.costo,
      recibidoPor: entrada.recibidoPor,
      usuarioId: autor.id,
      usuarioNombre: autor.nombre,
      observaciones: entrada.observaciones,
      fecha: lote.ingresado,
    }

    set((s) => ({
      productos: s.productos.map((x) => (x.id === p.id ? actualizado : x)),
      lotes: [lote, ...s.lotes],
      movimientos: [movimiento, ...s.movimientos],
    }))

    await Promise.all([
      adaptador.guardar('productos', [actualizado]),
      adaptador.guardar('lotes', [lote]),
      adaptador.guardar('movimientos', [movimiento]),
    ])
    await get().anotar(
      autor,
      'ingreso_stock',
      'producto',
      `+${entrada.cantidad} de ${p.nombre}`,
      p.id,
    )
  },

  async registrarOrdenIngreso(entrada, autor) {
    const { productos, adaptador, lotes } = get()
    if (!entrada.lineas.length) throw new Error('Agrega al menos un producto.')

    const productosTocados: Producto[] = []
    const lotesNuevos: Lote[] = []
    const movimientos: Movimiento[] = []
    const fecha = entrada.fecha ? new Date(entrada.fecha).toISOString() : ahora()
    let siguienteNumeroLote = lotes.length + 1

    for (const l of entrada.lineas) {
      const p = productos.find((x) => x.id === l.productoId)
      if (!p) throw new Error('Uno de los productos ya no existe.')
      if (l.cantidad <= 0) throw new Error(`${p.nombre}: la cantidad debe ser mayor que cero.`)

      const actualizado: Producto = {
        ...p,
        stock: p.stock + l.cantidad,
        // El último costo pagado manda: es el que refleja el margen real.
        precioCosto: l.costo > 0 ? l.costo : p.precioCosto,
      }
      productosTocados.push(actualizado)

      const lote: Lote = {
        id: uid('l'),
        productoId: p.id,
        codigo: l.codigoLote?.trim() || `L${new Date().getFullYear()}-${String(siguienteNumeroLote++).padStart(3, '0')}`,
        cantidad: l.cantidad,
        cantidadInicial: l.cantidad,
        vencimiento: l.vencimiento || undefined,
        costo: l.costo || p.precioCosto,
        proveedor: entrada.proveedor,
        recibidoPor: entrada.recibidoPor,
        ingresado: fecha,
      }
      lotesNuevos.push(lote)

      movimientos.push({
        id: uid('m'),
        productoId: p.id,
        tipo: 'ingreso',
        cantidad: l.cantidad,
        stockResultante: actualizado.stock,
        loteId: lote.id,
        proveedor: entrada.proveedor,
        costo: l.costo,
        recibidoPor: entrada.recibidoPor,
        usuarioId: autor.id,
        usuarioNombre: autor.nombre,
        observaciones: entrada.observaciones,
        fecha,
      })
    }

    set((s) => ({
      productos: s.productos.map((p) => productosTocados.find((x) => x.id === p.id) ?? p),
      lotes: [...lotesNuevos, ...s.lotes],
      movimientos: [...movimientos, ...s.movimientos],
    }))

    await Promise.all([
      adaptador.guardar('productos', productosTocados),
      adaptador.guardar('lotes', lotesNuevos),
      adaptador.guardar('movimientos', movimientos),
    ])

    const totalUnidades = entrada.lineas.reduce((s, l) => s + l.cantidad, 0)
    await get().anotar(
      autor,
      'ingreso_stock',
      'producto',
      `Orden recibida: ${entrada.lineas.length} ${entrada.lineas.length === 1 ? 'producto' : 'productos'} · ${totalUnidades} unidades${entrada.proveedor ? ` · ${entrada.proveedor}` : ''}`,
    )
  },

  async ajustarStock(productoId, nuevoStock, motivo, autor) {
    const { productos, adaptador } = get()
    const p = productos.find((x) => x.id === productoId)
    if (!p) return
    const delta = nuevoStock - p.stock
    if (delta === 0) return
    const actualizado = { ...p, stock: nuevoStock }
    const movimiento: Movimiento = {
      id: uid('m'),
      productoId,
      tipo: delta < 0 ? 'merma' : 'ajuste',
      cantidad: delta,
      stockResultante: nuevoStock,
      usuarioId: autor.id,
      usuarioNombre: autor.nombre,
      observaciones: motivo,
      fecha: ahora(),
    }
    set((s) => ({
      productos: s.productos.map((x) => (x.id === productoId ? actualizado : x)),
      movimientos: [movimiento, ...s.movimientos],
    }))
    await Promise.all([
      adaptador.guardar('productos', [actualizado]),
      adaptador.guardar('movimientos', [movimiento]),
    ])
    await get().anotar(autor, 'ajuste', 'producto', `${p.nombre}: ${delta > 0 ? '+' : ''}${delta} — ${motivo}`, productoId)
  },

  async registrarDevolucion(entrada, autor) {
    const { ventas, productos, adaptador } = get()
    const venta = ventas.find((v) => v.id === entrada.ventaId)
    const producto = productos.find((p) => p.id === entrada.productoId)
    if (!venta || !producto) throw new Error('No se encontró la venta o el producto.')
    const linea = venta.lineas.find((l) => l.productoId === entrada.productoId)
    if (!linea) throw new Error('Ese producto no está en la venta.')
    if (entrada.cantidad > linea.cantidad) {
      throw new Error(`La venta solo incluye ${linea.cantidad} unidades.`)
    }

    const devolucion: Devolucion = {
      id: uid('d'),
      ventaId: venta.id,
      ventaFolio: venta.folio,
      productoId: producto.id,
      productoNombre: producto.nombre,
      cantidad: entrada.cantidad,
      monto: linea.precioUnitario * entrada.cantidad,
      motivo: entrada.motivo,
      reponeStock: entrada.reponeStock,
      usuarioId: autor.id,
      usuarioNombre: autor.nombre,
      fecha: ahora(),
    }

    const actualizado = entrada.reponeStock
      ? { ...producto, stock: producto.stock + entrada.cantidad }
      : producto

    const movimiento: Movimiento = {
      id: uid('m'),
      productoId: producto.id,
      tipo: entrada.reponeStock ? 'devolucion' : 'merma',
      cantidad: entrada.reponeStock ? entrada.cantidad : 0,
      stockResultante: actualizado.stock,
      referenciaId: devolucion.id,
      usuarioId: autor.id,
      usuarioNombre: autor.nombre,
      observaciones: `Devolución ${venta.folio}: ${entrada.motivo}`,
      fecha: devolucion.fecha,
    }

    set((s) => ({
      devoluciones: [devolucion, ...s.devoluciones],
      productos: s.productos.map((p) => (p.id === producto.id ? actualizado : p)),
      movimientos: [movimiento, ...s.movimientos],
    }))
    await Promise.all([
      adaptador.guardar('devoluciones', [devolucion]),
      adaptador.guardar('productos', [actualizado]),
      adaptador.guardar('movimientos', [movimiento]),
    ])
    await get().anotar(
      autor,
      'devolucion',
      'venta',
      `${entrada.cantidad} × ${producto.nombre} de ${venta.folio}`,
      venta.id,
    )
  },

  /* ------------------------ Mantenedores ------------------------ */

  async guardarProducto(producto, autor) {
    const anterior = get().productos.find((p) => p.id === producto.id)
    const existe = Boolean(anterior)
    set((s) => ({
      productos: existe
        ? s.productos.map((p) => (p.id === producto.id ? producto : p))
        : [producto, ...s.productos],
    }))

    // Si el stock cambió a mano desde el formulario (no desde "Ajustar
    // stock"), igual queda un movimiento — si no, el historial mentiría
    // sobre de dónde salió la diferencia.
    const delta = anterior ? producto.stock - anterior.stock : 0
    const movimiento: Movimiento | null =
      delta !== 0
        ? {
            id: uid('m'),
            productoId: producto.id,
            tipo: delta < 0 ? 'merma' : 'ajuste',
            cantidad: delta,
            stockResultante: producto.stock,
            usuarioId: autor.id,
            usuarioNombre: autor.nombre,
            observaciones: 'Editado desde la ficha del producto.',
            fecha: ahora(),
          }
        : null
    if (movimiento) set((s) => ({ movimientos: [movimiento, ...s.movimientos] }))

    await Promise.all([
      get().adaptador.guardar('productos', [producto]),
      movimiento ? get().adaptador.guardar('movimientos', [movimiento]) : Promise.resolve(),
    ])
    await get().anotar(
      autor,
      existe ? 'editar' : 'crear',
      'producto',
      producto.nombre,
      producto.id,
    )
  },

  async eliminarProducto(id, autor) {
    const p = get().productos.find((x) => x.id === id)
    set((s) => ({ productos: s.productos.filter((x) => x.id !== id) }))
    await get().adaptador.eliminar('productos', [id])
    await get().anotar(autor, 'eliminar', 'producto', p?.nombre ?? id, id)
  },

  async guardarCliente(cliente, autor) {
    const existe = get().clientes.some((c) => c.id === cliente.id)
    set((s) => ({
      clientes: existe
        ? s.clientes.map((c) => (c.id === cliente.id ? cliente : c))
        : [cliente, ...s.clientes],
    }))
    await get().adaptador.guardar('clientes', [cliente])
    await get().anotar(autor, existe ? 'editar' : 'crear', 'cliente', cliente.nombre, cliente.id)
  },

  async eliminarCliente(id, autor) {
    const c = get().clientes.find((x) => x.id === id)
    set((s) => ({ clientes: s.clientes.filter((x) => x.id !== id) }))
    await get().adaptador.eliminar('clientes', [id])
    await get().anotar(autor, 'eliminar', 'cliente', c?.nombre ?? id, id)
  },

  async guardarUsuario(usuario, autor) {
    const existe = get().usuarios.some((u) => u.id === usuario.id)
    set((s) => ({
      usuarios: existe
        ? s.usuarios.map((u) => (u.id === usuario.id ? usuario : u))
        : [...s.usuarios, usuario],
    }))
    await get().adaptador.guardar('usuarios', [usuario])
    await get().anotar(autor, existe ? 'editar' : 'crear', 'usuario', usuario.nombre, usuario.id)
  },

  async eliminarUsuario(id, autor) {
    const u = get().usuarios.find((x) => x.id === id)
    set((s) => ({ usuarios: s.usuarios.filter((x) => x.id !== id) }))
    await get().adaptador.eliminar('usuarios', [id])
    await get().anotar(autor, 'eliminar', 'usuario', u?.nombre ?? id, id)
  },

  async guardarAjustes(ajustes, autor) {
    set({ ajustes })
    await get().adaptador.guardarAjustes(ajustes)
    await get().anotar(autor, 'editar', 'ajustes', 'Se actualizó la configuración')
  },

  /* --------------------------- Gastos ---------------------------- */

  async guardarGasto(gasto, autor) {
    const existe = get().gastos.some((g) => g.id === gasto.id)
    set((s) => ({
      gastos: existe ? s.gastos.map((g) => (g.id === gasto.id ? gasto : g)) : [gasto, ...s.gastos],
    }))
    await get().adaptador.guardar('gastos', [gasto])
    await get().anotar(
      autor,
      existe ? 'editar' : 'crear',
      'gasto',
      `${gasto.categoria} · ${gasto.descripcion}`,
      gasto.id,
    )
  },

  async eliminarGasto(id, autor) {
    const g = get().gastos.find((x) => x.id === id)
    set((s) => ({ gastos: s.gastos.filter((x) => x.id !== id) }))
    await get().adaptador.eliminar('gastos', [id])
    await get().anotar(autor, 'eliminar', 'gasto', g?.descripcion ?? id, id)
  },

  /* -------------------------- Bitácora -------------------------- */

  async anotar(autor, accion, entidad, detalle, entidadId) {
    const registro: Bitacora = {
      id: uid('b'),
      usuarioId: autor.id,
      usuarioNombre: autor.nombre,
      accion,
      entidad,
      entidadId,
      detalle,
      fecha: ahora(),
    }
    set((s) => ({ bitacora: [registro, ...s.bitacora].slice(0, 5000) }))
    await get().adaptador.guardar('bitacora', [registro])
  },

  /* -------------------------- Respaldo -------------------------- */

  exportarRespaldo() {
    const {
      usuarios,
      productos,
      lotes,
      movimientos,
      clientes,
      ventas,
      devoluciones,
      bitacora,
      gastos,
      ajustes,
    } = get()
    return {
      usuarios,
      productos,
      lotes,
      movimientos,
      clientes,
      ventas,
      devoluciones,
      bitacora,
      gastos,
      ajustes,
    }
  },

  async restaurarRespaldo(snapshot) {
    await get().adaptador.reemplazarTodo(snapshot)
    set({ ...ordenar(snapshot) })
  },

  async cargarDemo() {
    const semilla = construirSemilla()
    await get().adaptador.reemplazarTodo(semilla)
    set({ ...ordenar(semilla) })
  },

  async borrarTodo() {
    const vacio = snapshotVacio()
    // Se conservan los usuarios: sin ellos nadie podría volver a entrar.
    vacio.usuarios = get().usuarios
    await get().adaptador.reemplazarTodo(vacio)
    set({ ...vacio })
  },
}))

/* ------------------------- Selectores ------------------------- */

type TablaGuardable = Tabla

export const tablasDisponibles: TablaGuardable[] = [
  'usuarios',
  'productos',
  'lotes',
  'movimientos',
  'clientes',
  'ventas',
  'devoluciones',
  'bitacora',
]

/** Alertas que el sistema levanta solo: stock bajo, vencimientos,
 *  créditos por cobrar y clientes a los que ya toca volver a ofrecer.
 *  Se calculan al vuelo desde el estado.
 *
 *  El aviso de "seguimiento" es personal: si se pasa `contexto`, un
 *  vendedor solo ve los suyos (los clientes que él mismo atendió la
 *  última vez); un administrador los ve todos. Sin contexto (por
 *  compatibilidad) se muestran todos. */
export function calcularNotificaciones(
  s: Snapshot,
  contexto?: { usuarioId: string; esAdmin: boolean },
): Notificacion[] {
  const avisos: Notificacion[] = []
  const umbralGlobal = s.ajustes.stockMinimoGlobal

  for (const p of s.productos) {
    if (!p.activo) continue
    const estado = estadoStock(p, umbralGlobal)
    if (estado === 'agotado') {
      avisos.push({
        id: `n_ago_${p.id}`,
        tipo: 'agotado',
        titulo: `${p.nombre} sin stock`,
        detalle: 'Reponer para poder seguir vendiéndolo.',
        enlace: `/inventario/${p.id}`,
        fecha: new Date().toISOString(),
        leida: false,
      })
    } else if (estado === 'poco') {
      avisos.push({
        id: `n_baj_${p.id}`,
        tipo: 'stock_bajo',
        titulo: `${p.nombre} con poco stock`,
        detalle: `Quedan ${p.stock - p.reservado} unidades.`,
        enlace: `/inventario/${p.id}`,
        fecha: new Date().toISOString(),
        leida: false,
      })
    }
  }

  for (const l of s.lotes) {
    if (!l.vencimiento || l.cantidad <= 0) continue
    const dias = diasHasta(l.vencimiento)
    if (dias <= s.ajustes.diasAvisoVencimiento) {
      const p = s.productos.find((x) => x.id === l.productoId)
      avisos.push({
        id: `n_ven_${l.id}`,
        tipo: 'vencimiento',
        titulo:
          dias < 0
            ? `${p?.nombre ?? 'Lote'} vencido`
            : `${p?.nombre ?? 'Lote'} vence en ${dias} ${dias === 1 ? 'día' : 'días'}`,
        detalle: `Lote ${l.codigo} · ${l.cantidad} unidades.`,
        enlace: `/inventario/${l.productoId}`,
        fecha: new Date().toISOString(),
        leida: false,
      })
    }
  }

  for (const v of s.ventas) {
    if (v.metodoPago !== 'credito' || v.pagado || v.estado === 'anulada') continue
    const dias = diasHasta(v.vencimientoPago)
    if (dias <= 3) {
      avisos.push({
        id: `n_cob_${v.id}`,
        tipo: 'cobranza',
        titulo: dias < 0 ? `${v.clienteNombre} con pago vencido` : `${v.clienteNombre} vence pronto`,
        detalle: `${v.folio} · ${v.total.toLocaleString('es-CL')} pesos.`,
        enlace: '/cobranza',
        fecha: new Date().toISOString(),
        leida: false,
      })
    }
  }

  for (const sg of seguimientosPendientes(s.ventas, s.clientes)) {
    if (contexto && !contexto.esAdmin && sg.usuarioId !== contexto.usuarioId) continue
    avisos.push({
      id: `n_seg_${sg.clienteId}`,
      tipo: 'seguimiento',
      titulo: `${sg.clienteNombre}: ya toca volver a ofrecer`,
      detalle: `Pide cada ${sg.frecuenciaPedidoDias} días · van ${sg.diasDesde} sin comprar.`,
      enlace: `/clientes/${sg.clienteId}`,
      fecha: new Date().toISOString(),
      leida: false,
    })
  }

  return avisos
}
